import { CampaignVarsVO } from "../marketing/google/CampaignVarsVO";
import { ClientFunnelGameStates } from "../tracking/clientFunnel/ClientFunnelGameStates";
import { ClientFunnelTrackingController } from "../tracking/clientFunnel/ClientFunnelTrackingController";
import { TrackingCache } from "../tracking/TrackingCache";
import { TimezoneUtil } from "../utils/TimezoneUtil";
import { BasicConstants } from "./constants/BasicConstants";

import { EnvironmentProvider } from "./environment/providers/EnvironmentProvider";
import { PathProvider } from "./environment/providers/PathProvider";
import { BasicModel } from "./model/BasicModel";
import { executeClientLoadTrackingEvent, executeImpressionTrackingEvent } from "./startup/ExecuteTrackingEvents";
import { detectFeatureBranchEnvironment } from "./startup/FeatureBranchDetector";
import { paramToBool } from "./startup/BasicURLVariables";
import { EnvGlobalsHandler } from "./EnvGlobalsHandler";
import { IEnvironmentGlobals } from "./IEnvironmentGlobals";
import { ExternalLog } from "../externalLogging/ExternalLog";
import { WorldAssingmentErrorFactory } from "../externalLogging/factories/WorldAssingmentErrorFactory";
import { PageIntegrationErrorFactory } from "../externalLogging/factories/PageIntegrationErrorFactory";
import { getQualifiedClassName, Capabilities, ExternalInterface } from "@goodgamestudios/createjs-utilities";
import { CoreLoggerConfigurator, LoggingLevel, getLogger } from "@goodgamestudios/logger";
import {
    CDN,
    WorldAssignment,
    WorldAssignmentError,
    WorldAssignmentErrorCodes,
    WorldAssignmentGlobals,
    INetworkInstance
} from "@goodgamestudios/world-assignment";
import * as Stats from "stats.js";

const logger = getLogger("CoreJS.BasicCacheBreakerSwf");

/**
 * BasicCacheBreakerSwf
 */
export class BasicCacheBreakerSwf {

    private _zoneCapacityLoaded: boolean;
    private _useDynamicWorldAssignment: boolean;
    private _worldAssignment: WorldAssignment;

    constructor() {
        this.initialize();
    }

    private initialize(): void {
        // Keep the start loading time in environment globals
        this.env.sessionStartTime = Date.now();

        this.loadURLVariables()
            .then(() => {
                this.initializeEnvironment();
                this.initializeLogger();
                this.initializeStats();

                this.initializeCookies()
                    .then(result => {
                        this.initializeCampaignData();
                        this.initializeEnvironmentProvider();
                        this.initTracking();
                        this.startWorldAssignment();
                    });
            });
    }

    /**
     * Load URL Variables from Integration Page and Query String from iframe.
     */
    private async loadURLVariables(): Promise<void> {
        await this.env.urlVariables.readQueryParametersFromLandingPage();
    }

    /**
     * Initialize environment based on parameters from URL Variables / Page integration and GET parameters
     */
    private initializeEnvironment(): void {
        let url: string = window.location.href;

        //----------------------------------
        //	check if client is in test folder or is started local
        //----------------------------------


        const params = this.env.urlVariables.urlParams;

        this.env.isLocalHost = BasicConstants.isLocalHost(url);

        // is test environment if either on localhost, or if the url contains both "test" and either "/qa/" or "/development/"
        const isTestEnvironment = this.env.isLocalHost ||
            (window.location.hostname.search("test") !== -1 &&
                (window.location.pathname.search("/qa/") !== -1 || window.location.pathname.search("/development/") !== -1) );

        const forceTestAsProduction = paramToBool(params.get("forceProductionEnv"));


        this.env.isTest = this.env.isLocal = forceTestAsProduction ? false : isTestEnvironment;

        // todo html5 we should get rid of all this things...
        this.env.isPrivateTestServer = url.indexOf(BasicConstants.PRIVATE_TEST_SERVER_URL) !== -1;
        this.env.isUsabilityTest = url.indexOf(BasicConstants.USABILITYTEST_FOLDER) !== -1;
        this.env.isPreClient = url.indexOf(BasicConstants.PRECLIENT_FOLDER) !== -1;
        this.env.isDevTest = url.indexOf(BasicConstants.DEV_TEST_FOLDER) !== -1;
        this.env.isLocaTest = url.indexOf(BasicConstants.LOCA_TEST_FOLDER) !== -1;

        // live test
        if (url.indexOf(BasicConstants.LIVE_TEST_FOLDER) !== -1) {
            this.env.isLiveTest = true;
        }

        //-----------------------------------------------
        // Detect if client is running on a live branching this.environment
        //-----------------------------------------------
        this.env.isLiveBranchingEnvironment = url.indexOf(BasicConstants.LIVE_BRANCHING_ENVIRONMENT) !== -1;

        //-----------------------------------------------
        // Check if client is running on a feature branch environment
        //-----------------------------------------------
        let {isFeatureBranch, id, environment} = detectFeatureBranchEnvironment(url);

        this.env.isFeatureBranchEnvironment = isFeatureBranch;
        this.env.featureBranchId = id;
        this.env.featureBranchEnvironment = environment;
    }

    /**
     * Initialize logger based on current environment.
     */
    private initializeLogger(): void {
        // set LoggingLevel based on current environment
        CoreLoggerConfigurator.setLevel(this.env.isTest ? LoggingLevel.DEBUG : LoggingLevel.WARN);
    }

    /**
     * Initialize Stats (from mrdoob)
     */
    private initializeStats(): void {
        if (!this.env.isTest) {
            return;
        }

        let stats = new Stats();

        document.body.appendChild(stats.dom);
        createjs.Ticker.addEventListener("tick", () => {
            stats.update();
        });
    }

    /**
     * Load URL Variables / Referrer from Integration Page and initialize cookies.
     *
     * Since all requests to ExternalInterface are executed asynchronously,
     * we need to ensure that each call is completed before the execution of the next call.
     *
     * In order of execution:
     * 1. Load JourneyHash and ensure that the value coming from LandingPage is properly stored.
     * 2. Load href from landing page.
     * 3. Load referrer from landing page.
     * 4. Load AccountCookie and use the JourneyHash as accountId.
     * 5. Load NetworkCookie and ensure that all player specific data is on memory.
     * 6. Load data from Page Integration and use it when available.
     */
    private async initializeCookies(): Promise<void> {
        await this.env.jsVariables.readVariablesFromJavaScript();
        await this.loadHref();
        await this.loadReferrer();
        //  fixme data loaded From Integration/LandingPage takes precedence over data available on LocalStorage ( we should skip steps if necessary)
        await this.loadAccountCookie();
        await this.loadNetworkCookie();
        await this.loadDataFromIntegrationPage();
    }

    /**
     * Load href (full url with parameters) fromn landing page.
     */
    private loadHref(): Promise<any> {
        logger.info("loadHref");
        if (!ExternalInterface.available) {
            logger.info("ExternalInterface not available...");
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            ExternalInterface.asyncCall("ggsGetHref")
                .then(result => {
                    logger.info(result);
                    this.env.href = result;
                    resolve();
                })
                .catch(reason => {
                    logger.info(reason);
                    resolve();
                });
        });
    }

    /**
     * Load referrer fromn landing page.
     */
    private loadReferrer(): Promise<any> {
        logger.info("loadReferrer");
        if (!ExternalInterface.available) {
            logger.info("ExternalInterface not available...");
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            ExternalInterface.asyncCall("ggsGetReferrer")
                .then(result => {
                    logger.info(result);
                    let url:URL = new URL(result);
                    this.env.referrer = url.origin;
                    resolve();
                })
                .catch(reason => {
                    logger.info(reason);
                    resolve();
                });
        });
    }

    /**
     * Load account cookie in order to define the accountId.
     *
     * Since the journey hash is used as a replacement for the accountId, it has to be loaded before the account cookie is initialized.
     */
    private loadAccountCookie(): Promise<any> {
        return new Promise((resolve) => {
            logger.info("loadAccountCookie");
            this.env.accountCookie.initialize().
                then(result => {
                    logger.info("AccountCookie loaded");

                    // Enable isFirstVisitOfGGS flag when is a new account
                    this.env.isFirstVisitOfGGS = this.env.accountCookie.isNewAccount;

                    // Check if a custom CDN is defined on the account cookie
                    if (this.env.accountCookie.cdn !== CDN.None) {
                        // Use custom CDN from account cookie
                        this.env.customCDN = this.env.accountCookie.cdn;
                    }

                    resolve();
                });
        });
    }

    /**
     * Load network cookie in order to initialize player specific data.
     */
    private loadNetworkCookie(): Promise<any> {
        return new Promise((resolve) => {
            logger.info("loadNetworkCookie");
            BasicModel.networkCookie.initialize().
                then(result => {
                    logger.info("NetworkCookie loaded");
                    resolve();
                });
        });
    }

    /**
     * Load data from Integration Page and use it to autologin the user.
     *
     * If no information is coming from Integration Page, continue the normal initialization process.
     */
    private loadDataFromIntegrationPage(): Promise<any> {
        if (!ExternalInterface.available) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.loadPageIntegrationCookies()
                .then(result => {
                    logger.info("got data from integration page", result);
                    resolve();
                })
                .catch(reason => {
                    logger.warn("got an error reading from IntegrationPage -  we will go on with normal initialization", reason);
                    // if data coming from cookies on integration page is not complete or corrupted or we have any other issue we dont want to crash initialization. do we?
                    // therefore we resolve and go on with the normal flow
                    resolve();
                });
        });
    }

    /**
     * in old flash core this method was called loadBrowserCookie
     */
    private loadPageIntegrationCookies(): Promise<any> {
        return new Promise((resolve, reject) => {

            const isBrowserCookieValid = (cookie) => {
                return cookie &&
                    cookie.instanceId != null &&
                    cookie.playerId != null &&
                    cookie.country != null &&
                    cookie.gameId != null &&
                    (
                        // email + username exist both ONLY if new player from landing page
                        //  if player already exists login in game or landingpage can happen with email or username - but that value is indifferently stored under username ( :facepalm )
                        //  if login was made using only
                        (cookie.username != null && cookie.loginToken != null) ||
                        (cookie.facebookUID != null)
                    );
            };

            ExternalInterface.asyncCall("ggsGetAutologinData")
                .then(result => {
                    ExternalInterface.call("ggsRemoveAutologinData");
                    ////////////
                    if (!isBrowserCookieValid(result)) {
                        resolve("no valid cookie found on integration page");
                        return;
                    }

                    BasicModel.networkCookie.instanceId = result.instanceId;
                    BasicModel.networkCookie.playerId = result.playerId;
                    BasicModel.networkCookie.country = result.country;

                    if (result.username != null && result.loginToken != null) {
                        if (result.email != null) {
                            BasicModel.networkCookie.email = result.email;
                        }
                        BasicModel.networkCookie.saveLoginData(result.username, result.loginToken);
                    } else {
                        BasicModel.networkCookie.facebookUID = result.facebookUID;
                    }
                    resolve("found cookies on integration page");
                })
                .catch(reason => {
                    logger.warn(reason);
                    reject(reason);
                });
        });
    }

    /**
     * Initialize marketing campaign data via URL parameters (if available)
     * Set marketing/campaign parameters. The variables came e.g. from Google AdWords campaigns or banner ads.
     * The variables will be attached to the HTML wrapper's url as GET parameters.
     */
    private initializeCampaignData(): void {
        this.env.initCampaignVars(
            new CampaignVarsVO(
                this.env.urlVariables.urlParams,
                getQualifiedClassName(this),
                CampaignVarsVO.SOURCE_URL_PARAMETER
            )
        );
    }

    /**
     * Initialize Environment Provider
     */
    private initializeEnvironmentProvider(): void {
        EnvironmentProvider.instance.clear();
        EnvironmentProvider.instance.globals = this.env;

        this.addGameSpecificEnvironments();

        if (this.customEnvironments) {
            for (let i = 0; i < this.customEnvironments.length; i++) {
                EnvironmentProvider.instance.add(this.customEnvironments[i]);
            }
        }
        //  no need to add any default/share/fake env.   only one env exist now and it's the relativePath environment added by the game
        // Detect environment
        EnvironmentProvider.instance.detect();
    }

    private initTracking(): void {
        TrackingCache.getInstance().init(this.env.gameId, this.env.networkId, this.env.referrer, this.env.accountId, Capabilities.language);
        TrackingCache.getInstance().useTestValidationServer(paramToBool(this.env.urlVariables.urlParams.get("validateTrackingEvents")));
    }

    private startWorldAssignment() {
        logger.info("start World Assignment");
        let config: WorldAssignmentGlobals = this.createWorldAssignmentConfig();
        this._worldAssignment = new WorldAssignment();
        this._worldAssignment.initialize(config);
        this._worldAssignment.start(ClientFunnelTrackingController.getInstance().trackState)
            .then(result => this.onWorldAssignmentComplete(result))
            .catch(error => this.onWorldAssignmentFail(error));
        // make worldassigment accessible to BasicFrameOne that is in another bundle
        window.ggs.worldAssignment = this._worldAssignment;
    }

    private createWorldAssignmentConfig(): WorldAssignmentGlobals {
        logger.debug(`env.isTest ${this.env.isTest}`);

        let config: WorldAssignmentGlobals = new WorldAssignmentGlobals();

        config.isTestEnvironment = this.env.isTest;

        config.forceToShowTestServers = this.env.urlVariables.forceToShowTestServers;
        config.useDynamicWorldAssignment = this._useDynamicWorldAssignment;
        config.geoIPUrl = this.geoIPUrl;
        config.countriesConfigUrl = PathProvider.instance.countryConfigURL;
        config.networkConfigUrl = PathProvider.instance.networkConfigURL;
        config.branchesConfigUrl = PathProvider.instance.branchesConfigURL;
        config.versionUrl = PathProvider.instance.versionURL;
        config.zoneCapacityUrl = PathProvider.instance.zoneCapacitiesConfigURL;
        config.browserLanguage = Capabilities.language;
        config.timezoneOffset = TimezoneUtil.getUTCTimezoneWithoutDST();
        config.countryCodeFromNetworkCookie = BasicModel.networkCookie.country;
        config.countryCodeFromQueryString = this.env.urlVariables.country;
        config.isHydraLogin = !!this.env.suk;
        config.fakeGeoIpResponse = null; //  TODO @HTML5  WHY   ?????
        config.alsoDetectUnavailableCountries = false;
        config.presetInstanceId = this.env.urlVariables.presetInstanceId;
        config.instanceIdFromNetworkCookie = BasicModel.networkCookie.instanceId;
        config.zoneIdFromNetworkCookie = parseFloat(BasicModel.networkCookie.zoneId);
        config.zoneIdFromFriendInviteCode = this.env.urlVariables.friendInviteZoneId;
        config.countryCodeFromFriendInviteCode = this.env.urlVariables.friendInviteInviterCountryCode;

        return config;
    }

    private onWorldAssignmentComplete(instance: INetworkInstance): void {
        logger.info("world assignment complete");

        // store instance
        BasicModel.networkCookie.instanceId = instance.instanceId;
        BasicModel.networkCookie.zoneId = instance.zoneId.toString();

        // Count new session
        this.env.accountCookie.newSession(
            this.env.buildNumberGame,
            BasicModel.networkCookie.clientVersion
        );

        this.env.allowedfullscreen = this._worldAssignment.facade.networkSettings.allowedFullScreen;
        this.env.defaultInstanceId = this._worldAssignment.facade.networkSettings.defaultInstanceId;
        this.env.earnCredits = this._worldAssignment.facade.networkSettings.earnCredits;
        this.env.enableFeedMessages = this._worldAssignment.facade.networkSettings.enableFeedMessages;
        this.env.enableLonelyCow = this._worldAssignment.facade.networkSettings.enableLonelyCow;
        this.env.invitefriends = this._worldAssignment.facade.networkSettings.inviteFriends;
        this.env.maxUsernameLength = this._worldAssignment.facade.networkSettings.maxUsernameLength;
        this.env.hasNetworkBuddies = this._worldAssignment.facade.networkSettings.networkBuddies;
        this.env.networknameString = this._worldAssignment.facade.networkSettings.networkName;
        this.env.requestPayByJS = this._worldAssignment.facade.networkSettings.requestPayByJS;
        this.env.showVersion = this._worldAssignment.facade.networkSettings.showVersion;
        this.env.useexternallinks = this._worldAssignment.facade.networkSettings.useExternalLinks;
        this.env.loginIsKeyBased = this._worldAssignment.facade.networkSettings.useKeyBasedLogin;
        this.env.usePayment = this._worldAssignment.facade.networkSettings.usePayment;

        // save current client version
        BasicModel.networkCookie.clientVersion = this.env.buildNumberGame;
        logger.debug(`build number  ${this.env.buildNumberGame} /  ${this.env.versionInformation.buildNumberGame}`);
        logger.debug(`semver  ${this.env.gamePackageVersion} / ${this.env.versionInformation.versionNumberGame}`);
        // load game
        this.loadGame();
    }

    private onWorldAssignmentFail(error: Error): void {
        let errorId: number = (<WorldAssignmentError>error).id || WorldAssignmentErrorCodes.UNKNOWN_ERROR;
        let errorMsg: string = error.message;

        logger.error(`WorldAssignment failed: ${errorMsg} (error id ${errorId})`);
        ExternalLog.logger.log(new WorldAssingmentErrorFactory(errorId, errorMsg));
    }

    protected loadGame(): void {
        logger.info("LOAD Game.bundle.js");

        // Track ClientLoadTrackingEvent, eventId: 30
        executeClientLoadTrackingEvent(this.env);

        // Track impression event, eventId: 8
        executeImpressionTrackingEvent(this.env);

        const script: HTMLScriptElement = document.createElement("script");
        const gameLink: HTMLElement = document.getElementById("Game");

        if (gameLink) {
            script.src = gameLink.getAttribute("href");
            script.onload = this.gameLoaded.bind(this);
            document.body.appendChild(script);
        } else {
            logger.error("Unable to find the HTML link element with id 'Game'.");
            ExternalLog.logger.log(new PageIntegrationErrorFactory(
                PageIntegrationErrorFactory.GAME_ELEMENT_MISSING,
                "Unable to find the HTML link element with id 'Game'."
            ));
        }
    }

    protected gameLoaded(): void {
        logger.info("Game.bundle.js WAS LOADED");
        ClientFunnelTrackingController.getInstance().trackState(ClientFunnelGameStates.CLIENT_START);
    }

    protected addGameSpecificEnvironments(): void {
        // for Override
    }

    protected get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }

    protected get geoIPUrl(): string {
        return BasicConstants.DOMAIN_PROTOCOL + "://i2c.goodgamestudios.com";
    }

    protected get customEnvironments(): any[] {
        return [];
    }

    protected set useDynamicWorldAssignment(value: boolean) {
        this._useDynamicWorldAssignment = value;
        this._zoneCapacityLoaded = !value;
    }
}
